import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
} from '@angular/core';
import { HttpResponse } from '@angular/common/http';

import { ProfileService } from '../../core/services/profile.service';
import { calendarReasonMessage } from '../../core/util/event-rsvp-state.util';

interface CalendarOptions {
  eventId: number;
  googleCalendarUrl: string;
  calendarFileName: string;
}

@Component({
  selector: 'app-calendar-picker',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './calendar-picker.component.html',
  styleUrl: './calendar-picker.component.scss',
})
export class CalendarPickerComponent implements OnDestroy {
  private static nextPanelId = 0;

  @Input({ required: true }) eventId!: number;
  @Input() buttonLabel = 'Add to calendar';
  @HostBinding('class.calendar-picker-host--full-width')
  @Input()
  fullWidth = false;
  @HostBinding('class.calendar-picker-host--anchored')
  @Input()
  anchored = false;

  isOpen = false;
  isLoading = false;
  isDownloading = false;
  error = '';
  options?: CalendarOptions;
  readonly panelId = `calendar-picker-panel-${++CalendarPickerComponent.nextPanelId}`;
  private listenersAttached = false;
  private readonly documentClickListener = (event: MouseEvent) =>
    this.onDocumentClick(event);
  private readonly documentKeydownListener = (event: KeyboardEvent) =>
    this.onDocumentKeydown(event);

  constructor(
    private profileService: ProfileService,
    private hostElement: ElementRef<HTMLElement>
  ) {}

  toggle(): void {
    if (this.isOpen) {
      this.close();
      return;
    }

    this.open();
  }

  open(): void {
    this.isOpen = true;
    this.error = '';
    this.attachDocumentListeners();
    if (this.isLoading || this.isDownloading) return;

    this.options = undefined;
    this.isLoading = true;
    this.profileService.getVirtualEventCalendarOptions(this.eventId).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        if (response?.success === 0 && response.data?.googleCalendarUrl) {
          this.options = response.data;
          return;
        }
        this.error = this.calendarError(response);
      },
      error: (error: any) => {
        this.isLoading = false;
        this.error = this.calendarError(error);
      },
    });
  }

  close(): void {
    this.isOpen = false;
    this.error = '';
    this.detachDocumentListeners();
    if (!this.isDownloading) {
      this.options = undefined;
    }
  }

  deferClose(): void {
    window.setTimeout(() => this.close());
  }

  onDocumentClick(event: MouseEvent): void {
    if (
      this.isOpen &&
      !event.composedPath().includes(this.hostElement.nativeElement)
    ) {
      this.close();
    }
  }

  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.isOpen || event.key !== 'Escape') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.close();
  }

  downloadCalendar(): void {
    if (this.isDownloading) return;

    this.error = '';
    this.isDownloading = true;
    this.profileService.downloadVirtualEventCalendar(this.eventId).subscribe({
      next: (response: HttpResponse<Blob>) => {
        this.isDownloading = false;
        const contentType = (
          response.headers.get('content-type') || ''
        ).toLowerCase();
        const calendar = response.body;
        if (
          response.status < 200 ||
          response.status >= 300 ||
          !calendar ||
          !contentType.startsWith('text/calendar')
        ) {
          this.error =
            'The calendar file could not be downloaded. Please try again.';
          return;
        }
        const url = URL.createObjectURL(calendar);
        const link = document.createElement('a');
        link.href = url;
        link.download =
          this.options?.calendarFileName || `citizex-event-${this.eventId}.ics`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        this.close();
      },
      error: async (error: any) => {
        this.error = await this.calendarBlobError(error);
        this.isDownloading = false;
      },
    });
  }

  ngOnDestroy(): void {
    this.detachDocumentListeners();
  }

  private attachDocumentListeners(): void {
    if (this.listenersAttached) return;

    const document = this.hostElement.nativeElement.ownerDocument;
    document.addEventListener('click', this.documentClickListener);
    document.addEventListener('keydown', this.documentKeydownListener, true);
    this.listenersAttached = true;
  }

  private detachDocumentListeners(): void {
    if (!this.listenersAttached) return;

    const document = this.hostElement.nativeElement.ownerDocument;
    document.removeEventListener('click', this.documentClickListener);
    document.removeEventListener('keydown', this.documentKeydownListener, true);
    this.listenersAttached = false;
  }

  private async calendarBlobError(error: any): Promise<string> {
    const body = error?.error;
    if (body instanceof Blob) {
      try {
        return this.calendarError(JSON.parse(await body.text()));
      } catch {
        return 'The calendar file could not be downloaded. Please try again.';
      }
    }
    return this.calendarError(error);
  }

  private calendarError(error: any): string {
    const reason = error?.message || error?.error?.message;
    return calendarReasonMessage(reason);
  }
}
